(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function findSizeVariantButton(container) {
    const buttons = container.querySelectorAll('variant-button[data-option-name]');
    for (let i = 0; i < buttons.length; i++) {
      const name = (buttons[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name === 'size') return buttons[i];
    }
    return null;
  }

  function getModalContent(modal) {
    return modal ? modal.querySelector('.foxkit-modal__content') : null;
  }

  function isSizeChartModalContent(content) {
    if (!content) return false;
    return !!(
      content.querySelector('#size-table') ||
      content.querySelector('.foxkit-sizechart') ||
      content.querySelector('[data-foxkit-sizechart]')
    );
  }

  function isSizeChartModal(modal) {
    return isSizeChartModalContent(getModalContent(modal));
  }

  function waitForSizeChartModalContent(modal, maxMs) {
    maxMs = maxMs || 2000;
    return new Promise(function (resolve) {
      const start = Date.now();
      function tick() {
        const content = getModalContent(modal);
        if (isSizeChartModalContent(content)) {
          resolve(content);
          return;
        }
        if (Date.now() - start >= maxMs) {
          resolve(null);
          return;
        }
        requestAnimationFrame(tick);
      }
      tick();
    });
  }

  function waitForSizeTable(content, maxMs) {
    maxMs = maxMs || 2000;
    return new Promise(function (resolve) {
      const start = Date.now();
      function tick() {
        const table = content.querySelector('#size-table') || document.getElementById('size-table');
        if (table && table.querySelector('tbody tr, thead tr')) {
          resolve(table);
          return;
        }
        if (Date.now() - start >= maxMs) {
          resolve(null);
          return;
        }
        requestAnimationFrame(tick);
      }
      tick();
    });
  }

  function waitForInchCmToggle(content, maxMs) {
    maxMs = maxMs || 2000;
    return new Promise(function (resolve) {
      const start = Date.now();
      function tick() {
        const controls = findInchCmControls(content);
        if (controls.table && controls.btnInch && controls.btnCm) {
          resolve(controls);
          return;
        }
        if (Date.now() - start >= maxMs) {
          resolve(controls.table ? controls : null);
          return;
        }
        requestAnimationFrame(tick);
      }
      tick();
    });
  }

  function fetchCartJson() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root + 'cart.json';
    if (typeof fetchJSON === 'function') {
      return fetchJSON(url);
    }
    return fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      return response.json();
    });
  }

  function normalizeSizeValue(value) {
    return (value || '').toString().trim().replace(/\s+/g, ' ');
  }

  function normalizeSizeKey(value) {
    return normalizeSizeValue(value).toLowerCase();
  }

  function findToggleButtonByText(scope, unit) {
    const candidates = scope.querySelectorAll(
      'button, [role="button"], .foxkit-sizechart-unit button, .size-unit-toggle button, .unit-toggle button, .unit-toggle span, .size-toggle button'
    );
    for (let i = 0; i < candidates.length; i++) {
      const text = normalizeSizeKey(candidates[i].textContent);
      if (text === unit) return candidates[i];
    }
    return null;
  }

  function findInchCmControls(content) {
    const table = content.querySelector('#size-table') || document.getElementById('size-table');
    const btnInch =
      content.querySelector('#btn-inch') ||
      content.querySelector('[data-unit="inch"]') ||
      findToggleButtonByText(content, 'inch');
    const btnCm =
      content.querySelector('#btn-cm') ||
      content.querySelector('[data-unit="cm"]') ||
      findToggleButtonByText(content, 'cm');
    return { table: table, btnInch: btnInch, btnCm: btnCm };
  }

  // The inch/cm toggle is pure size-chart UI, so it lives at module scope and is
  // bound for every product — including ones whose variant options are not named
  // "Size" (e.g. the T-shirt bra's BAND/CUP picker), which bail out of the
  // add-to-cart enhancement below. `syncRadiosHook` is wired up by that
  // enhancement when it does run, so table radios stay in sync with the unit.
  let syncRadiosHook = null;

  function applyInchCmUnit(table, unit) {
    const isInch = unit === 'inch';
    const inchRows = table.querySelectorAll('tbody .inch-row');
    const cmRows = table.querySelectorAll('tbody .cm-row');
    const hasPairedRows = inchRows.length > 0 || cmRows.length > 0;

    table.classList.toggle('inch', isInch);
    table.classList.toggle('cm', !isInch);

    if (hasPairedRows) {
      inchRows.forEach(function (row) {
        row.classList.toggle('active', isInch);
      });
      cmRows.forEach(function (row) {
        row.classList.toggle('active', !isInch);
      });
    }

    table.querySelectorAll('.inch-col, .inch-cell, [data-unit="inch"]').forEach(function (el) {
      if (el.tagName === 'TD' || el.tagName === 'TH' || el.classList.contains('inch-col') || el.classList.contains('inch-cell')) {
        el.style.display = isInch ? '' : 'none';
      }
    });
    table.querySelectorAll('.cm-col, .cm-cell, [data-unit="cm"]').forEach(function (el) {
      if (el.tagName === 'TD' || el.tagName === 'TH' || el.classList.contains('cm-col') || el.classList.contains('cm-cell')) {
        el.style.display = isInch ? 'none' : '';
      }
    });

    if (typeof syncRadiosHook === 'function') syncRadiosHook(table);
  }

  function bindInchCmToggle(content) {
    // Marked synchronously: the add-to-cart init and the standalone observer can
    // both reach this for the same modal, and the await below would otherwise let
    // both get past an async-only check and attach duplicate listeners.
    if (content.dataset.sizeChartInchCmBound) return;
    content.dataset.sizeChartInchCmBound = 'pending';

    return waitForInchCmToggle(content).then(function (controls) {
      if (!controls || !controls.table || !controls.btnInch || !controls.btnCm) {
        delete content.dataset.sizeChartInchCmBound;
        return;
      }

      content.dataset.sizeChartInchCmBound = 'true';

      const target = controls.table;
      const btnInch = controls.btnInch;
      const btnCm = controls.btnCm;

      if (!target.classList.contains('active')) {
        target.classList.add('active');
      }

      function setUnit(unit) {
        const isInch = unit === 'inch';
        btnCm.classList.toggle('active', !isInch);
        btnInch.classList.toggle('active', isInch);
        applyInchCmUnit(target, unit);
      }

      btnInch.addEventListener('click', function () {
        setUnit('inch');
      });

      btnCm.addEventListener('click', function () {
        setUnit('cm');
      });

      if (btnCm.classList.contains('active')) {
        setUnit('cm');
      } else {
        setUnit('inch');
      }
    });
  }

  function getLiveVariantPickerContainer() {
    return document.querySelector('[data-variant-picker]');
  }

  function getLiveProductFormEl() {
    return document.querySelector('.m-product-form--main');
  }

  function getLiveSizeVariantButton() {
    const container = getLiveVariantPickerContainer();
    return container ? findSizeVariantButton(container) : null;
  }

  function getLiveVariantPicker() {
    const container = getLiveVariantPickerContainer();
    return container ? container.querySelector('variant-picker') : null;
  }

  onReady(function initSizeChartAtc() {
    const variantPickerContainer = getLiveVariantPickerContainer();
    const productFormEl = getLiveProductFormEl();

    if (!variantPickerContainer || !productFormEl) return;

    const sizeVariantButton = findSizeVariantButton(variantPickerContainer);
    if (!sizeVariantButton) return;

    const variantPicker = variantPickerContainer.querySelector('variant-picker');
    if (!variantPicker) return;

    const productId = variantPickerContainer.dataset.productId;
    syncRadiosHook = function (table) {
      syncTableRadios(table);
    };
    const getProductFormEl = getLiveProductFormEl;
    const getVariantPicker = getLiveVariantPicker;
    const getSizeVariantButton = getLiveSizeVariantButton;
    let activeFooter = null;
    let activeModal = null;
    let activeTable = null;
    let pendingFooterAdd = false;
    let mainAtcObserver = null;
    let cartVariantIds = new Set();
    let sizeGuideRequested = false;
    let sizeGuideRequestTimer = null;

    function markSizeGuideRequested() {
      sizeGuideRequested = true;
      if (sizeGuideRequestTimer) clearTimeout(sizeGuideRequestTimer);
      sizeGuideRequestTimer = setTimeout(function () {
        sizeGuideRequested = false;
        sizeGuideRequestTimer = null;
      }, 4000);
    }

    function clearSizeGuideRequest() {
      sizeGuideRequested = false;
      if (sizeGuideRequestTimer) {
        clearTimeout(sizeGuideRequestTimer);
        sizeGuideRequestTimer = null;
      }
    }

    function getMainAtcButton() {
      const form = getProductFormEl();
      return form ? form.querySelector('.m-add-to-cart') : null;
    }

    function isSizeSelectionRequired() {
      const picker = getVariantPicker();
      const sizeButton = getSizeVariantButton();
      if (!sizeButton) return true;
      if (picker && picker.sizeNotSelected === true) return true;
      const selectedValue = sizeButton.getAttribute('data-selected-value');
      if (!selectedValue) return true;
      const checkedInput = sizeButton.querySelector('input[type="radio"]:checked');
      if (!checkedInput) return true;
      return false;
    }

    function getSelectedSizeValue() {
      if (isSizeSelectionRequired()) return null;
      const sizeButton = getSizeVariantButton();
      return sizeButton ? sizeButton.getAttribute('data-selected-value') : null;
    }

    function getSelectedVariantId() {
      const form = getProductFormEl();
      const input = form ? form.querySelector('[name="id"]') : null;
      return input ? String(input.value) : null;
    }

    function isVariantInCart(variantId) {
      if (!variantId) return false;
      return cartVariantIds.has(String(variantId));
    }

    function getViewCartLabel() {
      return (window.MinimogStrings && window.MinimogStrings.viewCart) || 'View Cart';
    }

    function getPdpSizeInput(sizeValue) {
      const sizeButton = getSizeVariantButton();
      if (!sizeButton) return null;
      const key = normalizeSizeKey(sizeValue);
      const inputs = sizeButton.querySelectorAll('input[type="radio"]');
      for (let i = 0; i < inputs.length; i++) {
        if (normalizeSizeKey(inputs[i].value) === key) return inputs[i];
      }
      return null;
    }

    function isPdpSizeValue(sizeValue) {
      return !!getPdpSizeInput(sizeValue);
    }

    function isPdpSizeSoldOut(sizeValue) {
      const input = getPdpSizeInput(sizeValue);
      return !!(input && input.disabled);
    }

    function refreshCartVariants() {
      return fetchCartJson()
        .then(function (cart) {
          cartVariantIds = new Set(
            (cart.items || []).map(function (item) {
              return String(item.variant_id);
            })
          );
          if (activeFooter) {
            const footerBtn = activeFooter.querySelector('.size-chart-atc-btn');
            if (footerBtn) updateFooterButton(footerBtn);
          }
        })
        .catch(function () {
          cartVariantIds = new Set();
        });
    }

    function openCart() {
      const settings = window.MinimogSettings || {};
      if (settings.enable_cart_drawer) {
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          return;
        }
        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
        }
        return;
      }
      window.location.href = (settings.routes && settings.routes.cart) || '/cart';
    }

    function updateFooterButton(footerBtn) {
      const footerText = footerBtn.querySelector('.m-add-to-cart--text');
      if (!footerText) return;

      const variantId = getSelectedVariantId();
      const inCart = isVariantInCart(variantId);

      footerBtn.classList.toggle('size-chart-atc-btn--view-cart', inCart);

      if (inCart) {
        footerBtn.disabled = false;
        footerBtn.classList.remove('disabled');
        footerBtn.dataset.footerAction = 'view-cart';
        footerText.textContent = getViewCartLabel();
        return;
      }

      footerBtn.dataset.footerAction = 'add-to-cart';
      const mainBtn = getMainAtcButton();
      if (!mainBtn) return;

      // When the selected variant is sold out, xflow hides the main ATC (adds its own
      // `!xflow-hidden` class) and shows a "Notify me" button. Mirror that here: turn the
      // footer into a Notify Me that opens the same xflow modal.
      if (mainBtn.className.indexOf('!xflow-hidden') !== -1) {
        footerBtn.disabled = false;
        footerBtn.classList.remove('disabled');
        footerBtn.classList.add('size-chart-atc-btn--notify');
        footerBtn.dataset.footerAction = 'notify';
        const xflowLabel = document.querySelector('xflow-bis-button .xflow-button, xflow-bis-button button');
        footerText.textContent =
          (xflowLabel && xflowLabel.textContent.trim()) || 'NOTIFY ME WHEN AVAILABLE';
        return;
      }

      footerBtn.classList.remove('size-chart-atc-btn--notify');
      footerBtn.disabled = mainBtn.disabled;
      footerBtn.classList.toggle('disabled', mainBtn.classList.contains('disabled'));
      footerText.textContent =
        (mainBtn.querySelector('.m-add-to-cart--text') || {}).textContent || footerText.textContent;
    }

    function selectSizeOnPdp(value) {
      const sizeButton = getSizeVariantButton();
      if (!sizeButton) return false;
      const inputs = sizeButton.querySelectorAll('input[type="radio"]');
      let targetInput = null;
      const key = normalizeSizeKey(value);
      inputs.forEach(function (input) {
        if (normalizeSizeKey(input.value) === key) targetInput = input;
      });
      if (!targetInput || targetInput.disabled) return false;

      targetInput.checked = true;
      sizeButton.setAttribute('data-selected-value', targetInput.value);
      const label = sizeButton.querySelector('.option-label--selected');
      if (label) label.textContent = targetInput.value;
      if (typeof window.setAcPdpSizeMemory === 'function') {
        window.setAcPdpSizeMemory(targetInput.value);
      }
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    function cleanupLegacyRadioColumn(table) {
      table.querySelectorAll('[data-size-chart-radio-header]').forEach(function (el) {
        el.remove();
      });
      table.querySelectorAll('[data-size-chart-radio-cell]').forEach(function (el) {
        el.remove();
      });
    }

    function getSizeColumnIndex(table) {
      const headerCells = table.querySelectorAll('thead th, thead td');
      for (let i = 0; i < headerCells.length; i++) {
        if (headerCells[i].hasAttribute('data-size-chart-radio-header')) continue;
        const text = normalizeSizeKey(headerCells[i].textContent);
        if (text === 'size' || text.indexOf('size') === 0) return i;
      }

      const firstBodyRow = table.querySelector('tbody tr');
      if (firstBodyRow) {
        const bodyHeaderCells = firstBodyRow.querySelectorAll('th, td');
        for (let j = 0; j < bodyHeaderCells.length; j++) {
          if (bodyHeaderCells[j].hasAttribute('data-size-chart-radio-cell')) continue;
          const bodyText = normalizeSizeKey(bodyHeaderCells[j].textContent);
          if (bodyText === 'size' || bodyText.indexOf('size') === 0) return j;
        }
      }

      return 0;
    }

    function getRowCells(row) {
      return Array.prototype.slice.call(row.querySelectorAll('th, td'));
    }

    function getAdjustedSizeColumnIndex(row, sizeColIndex) {
      const cells = getRowCells(row);
      let index = sizeColIndex;
      if (cells[0] && cells[0].hasAttribute('data-size-chart-radio-cell')) {
        index += 1;
      }
      return index;
    }

    function readSizeLabelFromCell(cell) {
      if (!cell) return null;
      const labelEl = cell.querySelector('[data-size-chart-size-label]');
      const text = normalizeSizeValue(labelEl ? labelEl.textContent : cell.textContent);
      return text || null;
    }

    function extractSizeFromRow(row, sizeColIndex) {
      const cells = getRowCells(row);
      const index = getAdjustedSizeColumnIndex(row, sizeColIndex);
      return readSizeLabelFromCell(cells[index]);
    }

    function isTableHeaderRow(row, sizeColIndex) {
      if (row.closest('thead')) return true;
      if (row.querySelector('th')) return true;

      const sizeValue = extractSizeFromRow(row, sizeColIndex);
      if (!sizeValue) return true;

      const key = normalizeSizeKey(sizeValue);
      if (key === 'size' || key === 'bra size' || key === 'over bust' || key === 'under bust') {
        return true;
      }

      return !isPdpSizeValue(sizeValue);
    }

    function isRowVisible(row) {
      if (!row || !row.isConnected) return false;
      if (row.classList.contains('inch-row') || row.classList.contains('cm-row')) {
        return row.classList.contains('active');
      }
      const style = window.getComputedStyle(row);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    function syncTableRadios(table) {
      if (!table) return;

      const selectedValue = getSelectedSizeValue();
      const selectedKey = selectedValue ? normalizeSizeKey(selectedValue) : null;

      table.querySelectorAll('[data-size-chart-radio]').forEach(function (radio) {
        const row = radio.closest('tr');
        const visible = !row || isRowVisible(row);
        const sizeValue = radio.value;
        const sizeKey = normalizeSizeKey(sizeValue);
        const soldOut = isPdpSizeSoldOut(sizeValue);
        const isSelected = !!(selectedKey && sizeKey === selectedKey);

        radio.disabled = soldOut || !visible;
        radio.checked = isSelected && !soldOut && visible;

        if (row) {
          row.classList.toggle('size-chart-atc-row--selected', isSelected && !soldOut && visible);
          row.classList.toggle('size-chart-atc-row--disabled', soldOut);
          row.setAttribute('aria-selected', isSelected && !soldOut && visible ? 'true' : 'false');
        }
      });
    }

    function onTableSizeSelect(sizeValue) {
      if (!sizeValue || isPdpSizeSoldOut(sizeValue)) return;
      if (!selectSizeOnPdp(sizeValue)) return;

      syncTableRadios(activeTable);
      const footerBtn = activeFooter && activeFooter.querySelector('.size-chart-atc-btn');
      if (footerBtn) updateFooterButton(footerBtn);
    }

    function ensureInlineRadioInSizeCell(sizeCell, sizeValue, row) {
      if (!sizeCell) return null;

      let wrap = sizeCell.querySelector('[data-size-chart-radio-wrap]');
      if (wrap) {
        const existingRadio = wrap.querySelector('[data-size-chart-radio]');
        if (existingRadio) return existingRadio;
      }

      const radioId =
        'size-chart-radio-' +
        normalizeSizeKey(sizeValue).replace(/[^a-z0-9]+/g, '-') +
        '-' +
        (row.classList.contains('cm-row') ? 'cm' : 'inch') +
        '-' +
        Math.random().toString(36).slice(2, 7);

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'size-chart-table-select';
      radio.id = radioId;
      radio.value = sizeValue;
      radio.className = 'size-chart-atc-radio';
      radio.setAttribute('data-size-chart-radio', '');
      radio.setAttribute('aria-label', 'Select size ' + sizeValue);

      const label = document.createElement('label');
      label.className = 'size-chart-atc-radio-label';
      label.setAttribute('for', radioId);
      label.appendChild(radio);

      wrap = document.createElement('span');
      wrap.className = 'size-chart-atc-size-cell';
      wrap.setAttribute('data-size-chart-radio-wrap', '');

      const sizeLabel = document.createElement('span');
      sizeLabel.className = 'size-chart-atc-size-label';
      sizeLabel.setAttribute('data-size-chart-size-label', '');

      if (sizeCell.querySelector('[data-size-chart-radio-wrap]')) {
        const oldWrap = sizeCell.querySelector('[data-size-chart-radio-wrap]');
        const oldLabel = oldWrap.querySelector('[data-size-chart-size-label]');
        if (oldLabel) {
          sizeLabel.textContent = oldLabel.textContent;
        }
        oldWrap.remove();
      } else {
        sizeLabel.textContent = readSizeLabelFromCell(sizeCell) || sizeValue;
        sizeCell.textContent = '';
      }

      wrap.appendChild(label);
      wrap.appendChild(sizeLabel);
      sizeCell.appendChild(wrap);

      radio.addEventListener('change', function () {
        if (radio.checked) onTableSizeSelect(sizeValue);
      });

      return radio;
    }

    function bindRowSelection(row, sizeValue) {
      if (row.dataset.sizeChartRowBound === 'true') return;
      row.dataset.sizeChartRowBound = 'true';

      row.addEventListener('click', function (event) {
        if (row.classList.contains('size-chart-atc-row--disabled')) return;
        if (!isRowVisible(row)) return;
        if (event.target.closest('a, button')) return;
        if (event.target.closest('.size-chart-atc-radio-label, input[type="radio"]')) return;
        onTableSizeSelect(sizeValue);
      });

      row.classList.add('size-chart-atc-row');
      row.setAttribute('role', 'option');
      row.setAttribute('tabindex', '0');
      row.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onTableSizeSelect(sizeValue);
      });
    }

    function injectTableRadios(table) {
      if (!table) return;

      cleanupLegacyRadioColumn(table);

      const sizeColIndex = getSizeColumnIndex(table);
      const rows = table.querySelectorAll('tbody tr');

      rows.forEach(function (row) {
        if (isTableHeaderRow(row, sizeColIndex)) return;

        const sizeValue = extractSizeFromRow(row, sizeColIndex);
        if (!sizeValue || !isPdpSizeValue(sizeValue)) return;

        const cells = getRowCells(row);
        const sizeCellIndex = getAdjustedSizeColumnIndex(row, sizeColIndex);
        const sizeCell = cells[sizeCellIndex];
        if (!sizeCell) return;

        ensureInlineRadioInSizeCell(sizeCell, sizeValue, row);
        bindRowSelection(row, sizeValue);
      });

      table.dataset.sizeChartRadiosBound = 'true';
      activeTable = table;
      syncTableRadios(table);
    }


    function watchMainAtc(footerBtn) {
      if (mainAtcObserver) {
        mainAtcObserver.disconnect();
        mainAtcObserver = null;
      }

      const mainBtn = getMainAtcButton();
      if (!mainBtn) return;

      updateFooterButton(footerBtn);

      mainAtcObserver = new MutationObserver(function () {
        updateFooterButton(footerBtn);
      });
      mainAtcObserver.observe(mainBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    function teardownFooter() {
      if (mainAtcObserver) {
        mainAtcObserver.disconnect();
        mainAtcObserver = null;
      }
      if (activeFooter) {
        activeFooter.remove();
        activeFooter = null;
      }
      if (activeModal) {
        const content = activeModal.querySelector('.foxkit-modal__content');
        if (content) {
          delete content.dataset.sizeChartInchCmBound;
          const table = content.querySelector('#size-table');
          if (table) {
            delete table.dataset.sizeChartRadiosBound;
            table.querySelectorAll('[data-size-chart-row-bound]').forEach(function (row) {
              delete row.dataset.sizeChartRowBound;
            });
          }
        }
        activeModal = null;
      }
      activeTable = null;
    }

    function buildFooter(content) {
      if (content.querySelector('[data-size-chart-atc]')) return;

      const footer = document.createElement('div');
      footer.className = 'size-chart-atc-footer';
      footer.setAttribute('data-size-chart-atc', '');

      const atcBtn = document.createElement('button');
      atcBtn.type = 'button';
      atcBtn.className =
        'm-add-to-cart m-button m-button--primary size-chart-atc-btn disabled';
      atcBtn.disabled = true;
      atcBtn.dataset.footerAction = 'add-to-cart';

      const atcText = document.createElement('span');
      atcText.className = 'm-add-to-cart--text';
      const mainBtn = getMainAtcButton();
      const mainTextEl = mainBtn && mainBtn.querySelector('.m-add-to-cart--text');
      atcText.textContent = mainTextEl ? mainTextEl.textContent.trim() : 'SELECT SIZE';
      atcBtn.appendChild(atcText);

      atcBtn.addEventListener('click', function () {
        if (atcBtn.disabled) return;

        if (atcBtn.dataset.footerAction === 'notify') {
          closeFoxKitModal();
          const xflowBtn = document.querySelector('xflow-bis-button .xflow-button, xflow-bis-button button');
          if (xflowBtn) xflowBtn.click();
          return;
        }

        if (atcBtn.dataset.footerAction === 'view-cart') {
          closeFoxKitModal();
          openCart();
          return;
        }

        pendingFooterAdd = true;
        const form = productFormEl.querySelector('form');
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else if (form) {
          form.submit();
        }
      });

      footer.appendChild(atcBtn);
      content.appendChild(footer);

      activeFooter = footer;
      watchMainAtc(atcBtn);
      refreshCartVariants();
    }

    async function setupTableSelection(content) {
      const table = await waitForSizeTable(content);
      if (!table) return;
      injectTableRadios(table);
    }

    async function onModalOpen(modal) {
      if (!modal || !modal.classList.contains('is-open')) return;
      // Foxkit reuses one shared .foxkit-modal element for every popup type
      // (size chart, quick view, bundle, pre-purchase, in-cart, related, etc.).
      // Only inject the size-chart ATC footer when the shopper explicitly opened
      // the size guide, so it never leaks onto the other popups.
      if (!sizeGuideRequested) return;

      const content = await waitForSizeChartModalContent(modal);
      if (!content) return;

      clearSizeGuideRequest();
      activeModal = modal;

      bindInchCmToggle(content);

      if (content.querySelector('[data-size-chart-atc]')) {
        activeFooter = content.querySelector('[data-size-chart-atc]');
        const legacyPills = activeFooter.querySelector('.size-chart-atc-sizes');
        if (legacyPills) legacyPills.remove();
        const footerBtn = activeFooter.querySelector('.size-chart-atc-btn');
        if (footerBtn) {
          updateFooterButton(footerBtn);
          watchMainAtc(footerBtn);
        }
        await setupTableSelection(content);
        refreshCartVariants();
        return;
      }

      buildFooter(content);
      await setupTableSelection(content);
    }

    function onModalClose(modal) {
      if (modal && activeModal && modal !== activeModal) return;
      teardownFooter();
      pendingFooterAdd = false;
      clearSizeGuideRequest();
    }

    function closeFoxKitModal() {
      const modal = activeModal || document.querySelector('.foxkit-modal.is-open');
      if (!modal) return;

      const closeBtn = modal.querySelector(
        '[data-foxkit-close], .foxkit-modal__close, .foxkit-modal-close, .foxkit-modal__header button'
      );
      if (closeBtn && typeof closeBtn.click === 'function') {
        closeBtn.click();
        return;
      }

      modal.classList.remove('is-open');
    }

    function closeCartDrawerIfOpen() {
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.close === 'function') {
        drawer.close();
      }
    }

    function suppressDrawerForFooterAdd() {
      if (!pendingFooterAdd || !activeModal || !activeModal.classList.contains('is-open')) {
        return;
      }

      closeCartDrawerIfOpen();
    }

    function extractAddedVariantId(cartResponse) {
      if (!cartResponse) return null;

      if (cartResponse.variant_id) {
        return String(cartResponse.variant_id);
      }

      const items = cartResponse.items;
      if (Array.isArray(items) && items.length) {
        const lastItem = items[items.length - 1];
        if (lastItem && lastItem.variant_id) {
          return String(lastItem.variant_id);
        }
      }

      return null;
    }

    function handleCartUpdate(cartResponse) {
      const addedVariantId = extractAddedVariantId(cartResponse);
      if (addedVariantId) {
        cartVariantIds.add(addedVariantId);
      }

      if (pendingFooterAdd && activeModal && activeModal.classList.contains('is-open')) {
        closeCartDrawerIfOpen();
        setTimeout(closeCartDrawerIfOpen, 0);
        setTimeout(closeCartDrawerIfOpen, 350);
        pendingFooterAdd = false;
      }

      refreshCartVariants();
    }

    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      const events = window.MinimogTheme.pubSubEvents;
      window.MinimogEvents.subscribe(events.openCartDrawer, suppressDrawerForFooterAdd);
      window.MinimogEvents.subscribe(events.cartUpdate, handleCartUpdate);
      window.MinimogEvents.subscribe(events.cartError, function () {
        pendingFooterAdd = false;
      });
    }

    if (productId && window.MinimogEvents) {
      window.MinimogEvents.subscribe(productId + '__VARIANT_CHANGE', function () {
        if (!activeFooter) return;
        const footerBtn = activeFooter.querySelector('.size-chart-atc-btn');
        if (footerBtn) updateFooterButton(footerBtn);
        syncTableRadios(activeTable);
      });
    }

    const bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (!(target instanceof HTMLElement)) return;
          if (!target.classList.contains('foxkit-modal')) return;

          if (target.classList.contains('is-open')) {
            onModalOpen(target);
          } else {
            onModalClose(target);
          }
          return;
        }

        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof HTMLElement)) return;
          const modal =
            node.matches && node.matches('.foxkit-modal.is-open')
              ? node
              : node.querySelector && node.querySelector('.foxkit-modal.is-open');
          if (modal) onModalOpen(modal);
        });
      });
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    // Flag a pending size-guide open from any size-guide trigger (buttons and
    // #open-size-guide anchors all carry data-open-sizeguide). Capture phase runs
    // before Foxkit opens the modal, so the flag is set when onModalOpen fires.
    // Delegated so dynamically-rendered triggers (e.g. quick view) are covered.
    document.addEventListener(
      'click',
      function (event) {
        if (!event.target || !event.target.closest) return;
        if (!event.target.closest('[data-open-sizeguide]')) return;
        markSizeGuideRequested();
      },
      true
    );

    refreshCartVariants();

    const openModal = document.querySelector('.foxkit-modal.is-open');
    if (openModal && isSizeChartModal(openModal)) {
      markSizeGuideRequested();
      onModalOpen(openModal);
    }
  });

  // Standalone inch/cm wiring: runs on every PDP, whether or not the add-to-cart
  // enhancement above initialised. Binding is idempotent (guarded by the
  // data-size-chart-inch-cm-bound flag), so the two paths can both fire safely.
  onReady(function initInchCmToggleOnly() {
    function handleModal(modal) {
      if (!modal || !modal.classList.contains('is-open')) return;
      waitForSizeChartModalContent(modal).then(function (content) {
        if (content) bindInchCmToggle(content);
      });
    }

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          const target = mutation.target;
          if (!(target instanceof HTMLElement)) return;
          if (!target.classList.contains('foxkit-modal')) return;
          if (target.classList.contains('is-open')) {
            handleModal(target);
          } else {
            const content = getModalContent(target);
            if (content) delete content.dataset.sizeChartInchCmBound;
          }
          return;
        }

        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof HTMLElement)) return;
          const modal =
            node.matches && node.matches('.foxkit-modal.is-open')
              ? node
              : node.querySelector && node.querySelector('.foxkit-modal.is-open');
          if (modal) handleModal(modal);
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    handleModal(document.querySelector('.foxkit-modal.is-open'));
  });
})();
